import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline-accent";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  iconEnd?: ReactNode;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon, iconEnd, loading = false, className, children, disabled, type = "button", ...rest },
  ref,
) {
  const classes = ["btn", variant !== "secondary" ? `btn--${variant}` : "", size !== "md" ? `btn--${size}` : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
  return (
    <button ref={ref} type={type} className={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <span className="spinner" aria-hidden="true" /> : icon}
      {children}
      {iconEnd}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Accessible name — required because the button only shows an icon. */
  label: string;
  variant?: "plain" | "bordered" | "primary";
  children: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = "plain", className, children, type = "button", title, ...rest },
  ref,
) {
  const classes = ["icon-btn", variant !== "plain" ? `icon-btn--${variant}` : "", className ?? ""].filter(Boolean).join(" ");
  return (
    <button ref={ref} type={type} className={classes} aria-label={label} title={title ?? label} {...rest}>
      {children}
    </button>
  );
});
